import { useState } from "react";
import { GENRES, REGIONS, YEAR_RANGES, type FilterState } from "@/utils/media";

interface FilterPanelProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-4 border-b border-background-200 last:border-b-0">
      <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function FilterPanel({ filters, onChange, onReset }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  const toggleGenre = (g: string) => {
    const next = filters.genres.includes(g)
      ? filters.genres.filter((x) => x !== g)
      : [...filters.genres, g];
    onChange({ ...filters, genres: next });
  };

  const activeCount =
    filters.genres.length + (filters.region ? 1 : 0) + (filters.yearLabel ? 1 : 0);

  const body = (
    <div>
      <Group title="类型">
        <div className="flex flex-wrap gap-2">
          {GENRES.map((g) => {
            const active = filters.genres.includes(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggleGenre(g)}
                className={`px-2.5 py-1 rounded-full text-xs cursor-pointer transition-colors whitespace-nowrap ${
                  active
                    ? "bg-accent-500 text-secondary-950 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {g}
              </button>
            );
          })}
        </div>
      </Group>

      <Group title="地区">
        <div className="flex flex-wrap gap-2">
          {REGIONS.map((r) => {
            const active = filters.region === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => onChange({ ...filters, region: active ? "" : r })}
                className={`px-3 py-1 rounded-full text-xs cursor-pointer transition-colors whitespace-nowrap ${
                  active
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {r}
              </button>
            );
          })}
        </div>
      </Group>

      <Group title="年份">
        <div className="grid grid-cols-3 gap-2">
          {YEAR_RANGES.map((y) => {
            const active = filters.yearLabel === y.label;
            return (
              <button
                key={y.label}
                type="button"
                onClick={() => onChange({ ...filters, yearLabel: active ? "" : y.label })}
                className={`px-2 py-1 rounded-md text-xs cursor-pointer transition-colors whitespace-nowrap ${
                  active
                    ? "bg-secondary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {y.label}
              </button>
            );
          })}
        </div>
      </Group>

      <button
        type="button"
        onClick={onReset}
        className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-md border border-background-200 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line"></i>
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 lg:p-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="lg:hidden w-full flex items-center justify-between cursor-pointer"
      >
        <span className="flex items-center gap-2 font-heading font-semibold text-foreground-900">
          <i className="ri-filter-3-line"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-secondary-950 text-xs">
              {activeCount}
            </span>
          )}
        </span>
        <i className={open ? "ri-arrow-up-s-line text-xl" : "ri-arrow-down-s-line text-xl"}></i>
      </button>

      <div className={`${open ? "block" : "hidden"} lg:block lg:mt-0`}>{body}</div>
    </div>
  );
}